import { cs } from '../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { DollarSign, Download, RefreshCw, Filter, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { useBusinessTimezone, getTodayInTz } from '../../utils/businessTime';
import { ParentLink, PlayerLink, EntityRefLink } from '../../components/admin/MoneyLinks';

const ENTITY_LABEL = {
  lane_booking: 'Lane Booking',
  tournament_registration: 'Tournament',
  enrollment: 'Enrollment',
  camp_registration: 'Camp',
  coaching_session: 'Coaching',
};

const METHOD_COLORS = {
  stripe: 'bg-blue-100 text-blue-700 border-blue-200',
  credit: 'bg-purple-100 text-purple-700 border-purple-200',
  cash: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const fmtMoney = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso, tz) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: tz || 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const csvEscape = (v) => {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function RefundsManagement() {
  const businessTz = useBusinessTimezone();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalsByMethod, setTotalsByMethod] = useState({});
  const [grandTotal, setGrandTotal] = useState(0);
  const [grandCount, setGrandCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 50;

  const [filters, setFilters] = useState({
    entity_type: 'all',
    method: 'all',
    customer_search: '',
    from_date: '',
    to_date: '',
  });

  const fetchRefunds = async (pageArg = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: pageArg * PAGE_SIZE };
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.method !== 'all') params.method = filters.method;
      if (filters.customer_search) params.customer_search = filters.customer_search;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const res = await api.get('/admin/refunds', { params });
      setRows(res.data?.rows || []);
      setTotalsByMethod(res.data?.totals_by_method || {});
      setGrandTotal(res.data?.grand_total || 0);
      setGrandCount(res.data?.grand_count || 0);
      setHasMore(Boolean(res.data?.has_more));
      setPage(pageArg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load refunds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = () => fetchRefunds(0);
  const handleResetFilters = () => {
    setFilters({ entity_type: 'all', method: 'all', customer_search: '', from_date: '', to_date: '' });
    setTimeout(() => fetchRefunds(0), 0);
  };

  const handleExportCSV = () => {
    if (!rows.length) {
      toast.info('Nothing to export');
      return;
    }
    const header = [
      'Refunded At',
      'Entity Type',
      'Summary',
      'Customer',
      'Email',
      'Amount',
      'Method',
      'Stripe Refund ID',
      'Reason',
      'Issued By',
      'Fully Refunded',
      'Entity Cancelled',
    ];
    const lines = rows.map((r) => [
      r.refunded_at,
      ENTITY_LABEL[r.entity_type] || r.entity_type,
      r.entity_summary,
      r.customer_name || '',
      r.customer_email || '',
      Number(r.amount || 0).toFixed(2),
      r.method,
      r.stripe_refund_id || '',
      r.reason || '',
      r.refunded_by_name || r.refunded_by || '',
      r.fully_refunded ? 'yes' : 'no',
      r.entity_cancelled ? 'yes' : 'no',
    ]);
    const csv = [header, ...lines].map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `refunds-${getTodayInTz(businessTz)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const methodTotals = useMemo(
    () => ({
      stripe: totalsByMethod.stripe || { total: 0, count: 0 },
      credit: totalsByMethod.credit || { total: 0, count: 0 },
      cash: totalsByMethod.cash || { total: 0, count: 0 },
    }),
    [totalsByMethod],
  );

  return (
    <DashboardLayout title="Refunds">
      <div className="space-y-6" data-testid="refunds-page">
        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">Total refunded</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="summary-grand-total">
                {fmtMoney(grandTotal)}
              </div>
              <div className="text-xs text-slate-500 mt-1">{grandCount} refund{grandCount === 1 ? '' : 's'}</div>
            </CardContent>
          </Card>
          <Card className="border border-blue-200 bg-blue-50/40">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-blue-700">Stripe</div>
              <div className="text-2xl font-bold text-blue-900 mt-1">{fmtMoney(methodTotals.stripe.total)}</div>
              <div className="text-xs text-blue-700 mt-1">{methodTotals.stripe.count} refund{methodTotals.stripe.count === 1 ? '' : 's'}</div>
            </CardContent>
          </Card>
          <Card className="border border-purple-200 bg-purple-50/40">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-purple-700">Account credit</div>
              <div className="text-2xl font-bold text-purple-900 mt-1">{fmtMoney(methodTotals.credit.total)}</div>
              <div className="text-xs text-purple-700 mt-1">{methodTotals.credit.count} refund{methodTotals.credit.count === 1 ? '' : 's'}</div>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50/40">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-emerald-700">Cash / offline</div>
              <div className="text-2xl font-bold text-emerald-900 mt-1">{fmtMoney(methodTotals.cash.total)}</div>
              <div className="text-xs text-emerald-700 mt-1">{methodTotals.cash.count} refund{methodTotals.cash.count === 1 ? '' : 's'}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Entity type</Label>
                <Select value={filters.entity_type} onValueChange={(v) => setFilters((f) => ({ ...f, entity_type: v }))}>
                  <SelectTrigger data-testid="filter-entity-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="lane_booking">Lane Bookings</SelectItem>
                    <SelectItem value="tournament_registration">Tournaments</SelectItem>
                    <SelectItem value="enrollment">Enrollments</SelectItem>
                    <SelectItem value="camp_registration">Camps</SelectItem>
                    <SelectItem value="coaching_session">Coaching</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={filters.method} onValueChange={(v) => setFilters((f) => ({ ...f, method: v }))}>
                  <SelectTrigger data-testid="filter-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="credit">Account credit</SelectItem>
                    <SelectItem value="cash">Cash / offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Customer (name or email)</Label>
                <Input
                  placeholder="Search…"
                  value={filters.customer_search}
                  onChange={(e) => setFilters((f) => ({ ...f, customer_search: e.target.value }))}
                  data-testid="filter-customer-search"
                />
              </div>
              <div>
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
                  data-testid="filter-from-date"
                />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
                  data-testid="filter-to-date"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button onClick={handleApplyFilters} data-testid="apply-filters-btn">
                <Filter className="h-4 w-4 mr-1" /> Apply
              </Button>
              <Button variant="outline" onClick={handleResetFilters} data-testid="reset-filters-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Reset
              </Button>
              <div className="ml-auto">
                <Button
                  variant="outline"
                  onClick={handleExportCSV}
                  disabled={!rows.length}
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4 mr-1" /> Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-amber-600" /> Refunds ({rows.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                <p className="text-slate-500 mt-3 text-sm">Loading refunds…</p>
              </div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">
                No refunds match the current filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Type</th>
                      <th className="text-left px-4 py-3">Customer</th>
                      <th className="text-left px-4 py-3">For</th>
                      <th className="text-right px-4 py-3">Amount</th>
                      <th className="text-left px-4 py-3">Method</th>
                      <th className="text-left px-4 py-3">Reason</th>
                      <th className="text-left px-4 py-3">Issued by</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50" data-testid={`refund-row-${r.id}`}>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{fmtDate(r.refunded_at, businessTz)}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="font-normal">
                            {ENTITY_LABEL[r.entity_type] || r.entity_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          <ParentLink
                            id={r.parent_id || r.customer_id}
                            name={r.customer_name}
                            email={r.customer_email}
                            testId={`refund-customer-${r.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="text-xs">
                            <EntityRefLink
                              route={r.entity_route}
                              entityType={r.entity_type}
                              label={r.entity_summary || r.entity_id}
                              testId={`refund-for-${r.id}`}
                            />
                          </div>
                          {r.player_name && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              Player: <PlayerLink id={r.player_id} name={r.player_name} />
                            </div>
                          )}
                          <div className="flex gap-2 mt-1">
                            {r.fully_refunded && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Fully refunded</Badge>
                            )}
                            {r.entity_cancelled && (
                              <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Cancelled</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtMoney(r.amount)}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`${METHOD_COLORS[r.method] || ''} text-[11px]`}>
                            {r.method === 'stripe' ? 'Stripe' : r.method === 'credit' ? 'Account credit' : 'Cash'}
                          </Badge>
                          {r.stripe_refund_id && (
                            <div className="text-[10px] text-slate-500 mt-1 font-mono truncate max-w-[120px]" title={r.stripe_refund_id}>
                              {r.stripe_refund_id}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate" title={r.reason || ''}>
                          {r.reason || '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{r.refunded_by_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && rows.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100" data-testid="refunds-pagination">
                <p className="text-xs text-slate-500">
                  Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length} of {grandCount}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0 || loading}
                    onClick={() => fetchRefunds(page - 1)}
                    data-testid="refunds-prev-btn"
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-slate-500">Page {page + 1}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasMore || loading}
                    onClick={() => fetchRefunds(page + 1)}
                    data-testid="refunds-next-btn"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
